/**
 * Cable effects.
 *
 * The whole of what this engine believes a cable can do to an analog signal,
 * in one file, derived from three numbers per cable and the impedances at each
 * end. If an effect is not computed here, the engine does not apply it.
 *
 * Two mechanisms exist:
 *
 *   SPEAKER CABLE -- series impedance
 *     The amplifier drives (Zout_amp + Rcable + jwLcable) in series with the
 *     loudspeaker. Because the loudspeaker impedance swings with frequency, the
 *     resulting divider is frequency dependent, and the deviation is largest
 *     where the load dips lowest. This is exactly the mechanism already modelled
 *     for amplifier output impedance, so the cable simply adds to it.
 *
 *   INTERCONNECT -- shunt capacitance
 *     Cable capacitance against the source output impedance forms a first-order
 *     low-pass. The corner is normally well above the audio band; the engine
 *     computes it rather than assuming, and reports what it finds.
 *
 * Power, USB and Ethernet have no mechanism and are computed as exact identity.
 * That is not an omission -- see CABLE_TYPE_RATIONALE in types/hardware.ts.
 */

import type { AmpProfile, CableProfile, DacProfile, OutputProfile } from '../types/hardware';
import { cableAffectsSignalPath } from '../types/hardware';
import type { FRCurve, FRPoint } from '../types/curves';
import { evalFR, evalImpedance, flat } from '../types/curves';
import { cableShuntCapacitance } from '../profiles/cableKit';

const GRID: number[] = [
  10, 16, 20, 25, 31.5, 40, 50, 63, 80, 100, 125, 160, 200, 250, 315, 400, 500,
  630, 800, 1000, 1250, 1600, 2000, 2500, 3150, 4000, 5000, 6300, 8000, 10000,
  12500, 16000, 20000,
];

const linToDb = (x: number) => 20 * Math.log10(Math.max(1e-12, x));

export interface CableEffectReport {
  /** Does the engine apply anything at all for this cable? */
  modelled: boolean;
  /** Worst-case in-band deviation from the 1 kHz reference, dB. */
  maxDeviationDb: number;
  /** Total series resistance of the run, ohms. Speaker cable only. */
  seriesResistanceOhm?: number;
  /** Total series inductance of the run, microhenries. Speaker cable only. */
  seriesInductanceUh?: number;
  /** Total shunt capacitance of the run, picofarads. Interconnect only. */
  shuntCapacitancePf?: number;
  /** First-order low-pass corner against the source impedance, Hz. Interconnect only. */
  cornerHz?: number;
  /** Damping factor including the cable, and without it, for comparison. */
  dampingWithCable?: number;
  dampingAmpOnly?: number;
  /** One line the inspector can print verbatim. */
  summary: string;
}

/**
 * Frequency response imposed by a speaker cable, normalised to 0 dB at 1 kHz.
 *
 * Returns a flat curve when there is no cable, when the run is trivially short,
 * or when the cable is not a speaker cable.
 */
export function speakerCableCurve(
  amp: AmpProfile | null,
  output: OutputProfile | null,
  cable: CableProfile | null,
  lengthM: number,
): FRCurve {
  if (!amp || !output || !cable || cable.cableType !== 'speaker' || lengthM <= 0) return flat();
  const z = loadImpedanceOf(output);
  if (!z) return flat();

  const zOut = amp.output.outputImpedanceOhms;
  const r = cable.electrical.resistanceOhmPerM * lengthM;
  const l = cable.electrical.inductanceUhPerM * 1e-6 * lengthM;

  /*
   * This returns the cable's MARGINAL contribution, not the whole divider.
   *
   * The compiler adds this curve on top of loadInteractionCurve(), which
   * already models |Z / (Zout + Z)| for the amplifier on its own. Returning the
   * full amp-plus-cable divider here would therefore apply the amplifier's
   * output impedance twice, which on a high-output-impedance amplifier driving
   * a loudspeaker with a swinging impedance curve is an error of many dB -- and
   * it would also make the reported "cable effect" a number about the amplifier
   * that barely moved when the cable length changed.
   *
   * So: compute the divider with the cable in series, compute it with the
   * amplifier alone, and return the difference. With a perfect zero-ohm
   * amplifier this reduces to the cable's own divider, which is correct;
   * loadInteractionCurve returns flat in that case and contributes nothing.
   */
  const divider = (f: number, sr: number, si: number) => {
    const { mag, phase } = evalImpedance(z, f);
    const th = (phase * Math.PI) / 180;
    const zr = mag * Math.cos(th);
    const zi = mag * Math.sin(th);
    const num = Math.hypot(zr, zi);
    const den = Math.hypot(zr + sr, zi + si);
    return linToDb(num / Math.max(1e-9, den));
  };
  const marginal = (f: number) => divider(f, zOut + r, 2 * Math.PI * f * l) - divider(f, zOut, 0);

  const ref = marginal(1000);
  return {
    kind: 'fr',
    points: GRID.map((f) => [f, Number((marginal(f) - ref).toFixed(4))] as FRPoint),
    interp: 'log-pchip',
    confidence: 'DERIVED',
    source: `Marginal effect of ${r.toFixed(4)} ohm and ${(l * 1e6).toFixed(2)} uH of ${lengthM} m cable added in series with a ${zOut.toFixed(3)} ohm amplifier output, against the transducer impedance curve`,
  };
}

/**
 * Frequency response imposed by an interconnect, normalised to 0 dB at 1 kHz.
 *
 * First-order low-pass formed by the source output impedance and the cable's
 * total shunt capacitance.
 */
export function interconnectCurve(
  dac: DacProfile | null,
  cable: CableProfile | null,
  lengthM: number,
  balanced: boolean,
): FRCurve {
  if (!dac || !cable || cable.cableType !== 'interconnect' || lengthM <= 0) return flat();
  const sourceOhm = dac.analog.outputImpedanceOhms ?? 100;
  if (sourceOhm <= 0) return flat();
  const c = cableShuntCapacitance(cable.electrical, lengthM);
  if (c <= 0) return flat();

  // A balanced interconnect carries two signal conductors; the capacitance each
  // sees to the shield is what loads the driver, so the effective loading per
  // phase is unchanged, but the driver impedance per phase is what matters.
  const rs = balanced ? sourceOhm : sourceOhm;
  const fc = 1 / (2 * Math.PI * rs * c);

  const at = (f: number) => linToDb(1 / Math.sqrt(1 + Math.pow(f / fc, 2)));
  const ref = at(1000);
  return {
    kind: 'fr',
    points: GRID.map((f) => [f, Number((at(f) - ref).toFixed(4))] as FRPoint),
    interp: 'log-pchip',
    confidence: 'DERIVED',
    source: `First-order low-pass from ${rs} ohm source impedance and ${(c * 1e12).toFixed(0)} pF of ${lengthM} m cable, corner ${(fc / 1000).toFixed(1)} kHz`,
  };
}

function loadImpedanceOf(output: OutputProfile) {
  // Re-uses the same impedance model the amplifier interaction uses, so a cable
  // and an amplifier output impedance are evaluated against identical data.
  if (output.category === 'speaker') return output.impedance;
  const hp = output;
  if (hp.impedance) return hp.impedance;
  if (hp.impedanceOhms) {
    return {
      kind: 'impedance' as const,
      points: [[10, hp.impedanceOhms, 0], [20000, hp.impedanceOhms, 0]] as Array<readonly [number, number, number]>,
      nominalOhms: hp.impedanceOhms,
    };
  }
  return null;
}

const maxDeviation = (c: FRCurve): number => {
  let worst = 0;
  for (const [f] of c.points) {
    if (f < 20 || f > 20000) continue;
    worst = Math.max(worst, Math.abs(evalFR(c, f)));
  }
  return worst;
};

/**
 * Everything the inspector needs to say about one cable, including the honest
 * "this does nothing" answer for the three types that have no mechanism.
 */
export function reportCable(
  cable: CableProfile | null,
  lengthM: number,
  ctx: { amp: AmpProfile | null; output: OutputProfile | null; dac: DacProfile | null; balanced: boolean },
): CableEffectReport {
  if (!cable) {
    return { modelled: false, maxDeviationDb: 0, summary: 'No cable selected. The engine treats this connection as ideal.' };
  }

  if (!cableAffectsSignalPath(cable.cableType)) {
    return {
      modelled: false,
      maxDeviationDb: 0,
      summary: `No signal processing is applied for a ${cable.cableType} cable. Output is bit-identical with this cable selected or with none.`,
    };
  }

  if (cable.cableType === 'speaker') {
    /*
     * A loudspeaker cable connected to a headphone chain is not a cable with a
     * small effect -- it is a cable that is not plugged into anything. Saying
     * "0.000 dB, below audibility" would imply it sits in the path and merely
     * does not matter, which is a different and false claim. Say what is true.
     */
    if (ctx.output && ctx.output.category !== 'speaker') {
      return {
        modelled: false,
        maxDeviationDb: 0,
        seriesResistanceOhm: cable.electrical.resistanceOhmPerM * lengthM,
        seriesInductanceUh: cable.electrical.inductanceUhPerM * lengthM,
        summary:
          'No signal processing is applied: the current output is not a loudspeaker, so this cable is not in the signal path at all. Select a loudspeaker to hear what it does.',
      };
    }
    const curve = speakerCableCurve(ctx.amp, ctx.output, cable, lengthM);
    const r = cable.electrical.resistanceOhmPerM * lengthM;
    const l = cable.electrical.inductanceUhPerM * lengthM;
    const nominal =
      ctx.output && ctx.output.category === 'speaker'
        ? (ctx.output.impedance.nominalOhms ?? 8)
        : 8;
    const zAmp = ctx.amp?.output.outputImpedanceOhms ?? 0;
    const dev = maxDeviation(curve);
    return {
      modelled: true,
      maxDeviationDb: dev,
      seriesResistanceOhm: r,
      seriesInductanceUh: l,
      dampingAmpOnly: zAmp > 0 ? nominal / zAmp : Infinity,
      dampingWithCable: zAmp + r > 0 ? nominal / (zAmp + r) : Infinity,
      summary:
        dev < 0.02
          ? `${lengthM} m adds ${r.toFixed(3)} ohm and ${l.toFixed(1)} uH in series. Computed in-band deviation is ${dev.toFixed(3)} dB -- below audibility.`
          : `${lengthM} m adds ${r.toFixed(3)} ohm and ${l.toFixed(1)} uH in series, producing up to ${dev.toFixed(2)} dB of response deviation against this loudspeaker's impedance curve.`,
    };
  }

  const curve = interconnectCurve(ctx.dac, cable, lengthM, ctx.balanced);
  const c = cableShuntCapacitance(cable.electrical, lengthM) * 1e12;
  const rs = ctx.dac?.analog.outputImpedanceOhms ?? 100;
  const fc = 1 / (2 * Math.PI * rs * (c * 1e-12));
  const dev = maxDeviation(curve);
  return {
    modelled: true,
    maxDeviationDb: dev,
    shuntCapacitancePf: c,
    cornerHz: fc,
    summary:
      dev < 0.02
        ? `${lengthM} m presents ${c.toFixed(0)} pF to a ${rs} ohm source, putting the low-pass corner at ${(fc / 1000).toFixed(0)} kHz. Computed in-band deviation is ${dev.toFixed(3)} dB -- below audibility.`
        : `${lengthM} m presents ${c.toFixed(0)} pF to a ${rs} ohm source, putting the corner at ${(fc / 1000).toFixed(1)} kHz and costing up to ${dev.toFixed(2)} dB in band.`,
  };
}
